<template>
  <DefaultCrudView
    @create-new="newRatecard"
    :breadcrumbsItems="breadcrumbs"
    :actions="crudActions"
    containerLarge
  >
    <template slot="content">

      <v-data-table
        :headers="headers"
        :items="ratecards"
        class="elevation-1 compact-table"
        :loading="loading"
        :options.sync="options"
        :server-items-length="itemsLength"
        :footer-props="$defaultDataTableFooterProps"
      >
        <template v-slot:[`item.tenant_id`]="{ item }">
          {{ getTenantTitle(item.tenant_id) }}
        </template>

        <template v-slot:[`item.active`]="{ item }">
          <v-chip small :color="item.active ? 'success' : 'error'" dark>
            {{ item.active ? $t('yes') : $t('no') }}
          </v-chip>
        </template>

        <template v-slot:[`item.created_at`]="{ item }">
          {{ formatDate(item.created_at) }}
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-menu offset-y left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>mdi-dots-horizontal</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="editRatecard(item)">
                <v-list-item-icon>
                  <v-icon medium>mdi-pencil</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ $t('edit') }}</v-list-item-title>
              </v-list-item>

              <v-divider></v-divider>

              <v-list-item v-if="item.active" @click="toggleActive(item, false)">
                <v-list-item-icon>
                  <v-icon medium>mdi-archive-off-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ $t('deactivate_ratecard') }}</v-list-item-title>
              </v-list-item>

              <v-list-item v-if="!item.active" @click="toggleActive(item, true)">
                <v-list-item-icon>
                  <v-icon medium>mdi-archive-check-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ $t('activate_ratecard') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>

      </v-data-table>

      <DefaultModal :title="getModalTitle()" ref="modal" @submit="save" @modal-closed="resetSelectedRatecard">
        <template v-slot:content>
          <DynamicFormContent :fields="fields"></DynamicFormContent>
        </template>
      </DefaultModal>

    </template>
  </DefaultCrudView>
</template>

<style scoped>
  .container {
    padding: 0;
  }

  .table-container {
    margin-top: 20px;
  }

  ::v-deep .compact-table .v-data-table__wrapper td,
  ::v-deep .compact-table .v-data-table__wrapper th {
    padding: 6px 8px !important;
  }
</style>

<script>
import { mapActions } from 'vuex'

import DefaultCrudView from '@/components/DefaultCrudView.vue'
import DefaultModal from '@/components/DefaultModal.vue'
import DynamicFormContent from '@/components/DynamicFormContent.vue'

import AppActions from '@/store/app/actions-types'
import TenantActions from '@/store/core/tenant/actions-types'

import RateCardAPI from '@/services/api/business/rate_card'

export default {
  name: 'BusinessRatecard',

  components: {
    DefaultCrudView,
    DefaultModal,
    DynamicFormContent,
  },

  computed: {
    breadcrumbs() {
      return [
        {
          text: this.$t('settings'),
          prependIcon: 'mdi-cog-outline',
          iconColor: 'primary',
          to: {
            name: 'DeliverableSettings',
          },
          disabled: false,
          exact: true,
          ripple: true,
        },
        {
          text: this.$t('ratecards'),
          prependIcon: 'mdi-card-text-outline',
        },
      ]
    },

    fields() {
      if (this.selectedRatecard) {
        // A rate card's tenant is immutable after creation.
        return [
          {
            key: 'tenant',
            name: 'tenant',
            label: this.$t('tenant'),
            type: 'text',
            value: this.getTenantTitle(this.selectedRatecard.tenant_id),
            readonly: true,
            cols: 12,
          },
          {
            key: 'rate_card_title',
            name: 'rate_card_title',
            label: this.$t('rate_card_title'),
            type: 'text',
            value: this.selectedRatecard.rate_card_title,
            required: true,
            rules: [v => !!v || this.$t('field_required')],
            cols: 12,
          },
          {
            key: 'active',
            name: 'active',
            label: this.$t('active'),
            type: 'switch',
            value: this.selectedRatecard.active,
            cols: 12,
          },
        ]
      }

      // Only offer tenants where the current user is an administrator.
      return [
        {
          key: 'tenant_id',
          name: 'tenant_id',
          label: this.$t('tenant'),
          type: 'select',
          value: '',
          required: true,
          rules: [v => !!v || this.$t('field_required')],
          items: this.adminTenants.map(tenant => ({
            text: tenant.title || tenant.tenant_title,
            value: tenant.tenant_id,
          })),
          itemText: 'text',
          itemValue: 'value',
          cols: 12,
        },
        {
          key: 'rate_card_title',
          name: 'rate_card_title',
          label: this.$t('rate_card_title'),
          type: 'text',
          value: '',
          required: true,
          rules: [v => !!v || this.$t('field_required')],
          cols: 12,
        },
      ]
    },

    adminTenants() {
      const userData = this.$store.state.core.auth.userData || {}
      const adminTenantIds = (userData.admins || []).map(admin => admin.tenant_id)

      return this.tenants.filter(tenant => adminTenantIds.includes(tenant.tenant_id))
    },
  },

  data() {
    return {
      loading: true,
      ratecards: [],
      options: {
        sortBy: ['rate_card_title'],
        sortDesc: [false],
      },
      itemsLength: 0,
      crudActions: [
        {
          text: 'create_ratecard',
          eventToEmit: 'create-new',
        },
      ],
      tenants: [],
      selectedRatecard: null,
      headers: [
        { text: this.$t('rate_card_title'), value: 'rate_card_title', sortable: true },
        { text: this.$t('tenant'), value: 'tenant_id', sortable: true },
        { text: this.$t('active'), value: 'active', sortable: true },
        { text: this.$t('created_at'), value: 'created_at', sortable: true },
        { text: this.$t('actions'), value: 'actions', sortable: false },
      ],
    }
  },

  mounted() {
    this.selectedRatecard = null

    this[TenantActions.GET_ALL_TENANTS]({ limit: 9999 })
      .then((tenants) => {
        this.tenants = tenants.items
      })
  },

  watch: {
    options: {
      handler() {
        this.reloadData()
      },
      deep: true,
    },
  },

  methods: {
    ...mapActions('core/tenant', [
      TenantActions.GET_ALL_TENANTS,
    ]),

    ...mapActions('app', [
      AppActions.OPEN_APP_SUCCESS_MESSAGE,
    ]),

    newRatecard() {
      this.selectedRatecard = null
      this.$refs.modal.openModal()
    },

    resetSelectedRatecard() {
      this.selectedRatecard = null
    },

    editRatecard(ratecard) {
      this.selectedRatecard = ratecard
      this.$refs.modal.openModal()
    },

    save() {
      if (!this.$refs.modal) {
        console.error('Modal reference not found')
        return
      }

      this.$refs.modal.errorMessage = null
      this.$refs.modal.errorList = []

      const formData = {}
      this.fields.forEach(field => {
        formData[field.name] = field.value
      })

      let apiCall

      if (this.selectedRatecard) {
        const rateCardId = this.selectedRatecard.rate_card_id

        if (!rateCardId) {
          console.error('Rate Card ID not found in selected ratecard object:', this.selectedRatecard)
          this.$refs.modal.errorMessage = this.$t('ratecard_id_not_found')
          return
        }

        apiCall = RateCardAPI.updateRateCard(rateCardId, {
          rate_card_title: formData.rate_card_title,
          active: formData.active,
        })
      } else {
        if (!formData.tenant_id) {
          this.$refs.modal.errorMessage = this.$t('tenant_required')
          this.$refs.modal.submitting = false
          return
        }

        apiCall = RateCardAPI.createRateCard({
          tenant_id: Number(formData.tenant_id),
          rate_card_title: formData.rate_card_title,
        })
      }

      apiCall
        .then(() => {
          const message = this.selectedRatecard
            ? this.$t('ratecard_updated_successfully')
            : this.$t('ratecard_created_successfully')
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](message)
          this.$refs.modal.closeModal()
          this.reloadData()
        })
        .catch((error) => {
          this.$refs.modal.submitting = false
          if (error.response && error.response.data) {
            this.$refs.modal.errorMessage = error.response.data.message || this.$t('error')
          }
        })
    },

    toggleActive(ratecard, activeValue) {
      const rateCardId = ratecard.rate_card_id

      if (!rateCardId) {
        console.error('Rate Card ID not found:', ratecard)
        return
      }

      RateCardAPI.updateRateCard(rateCardId, {
        rate_card_title: ratecard.rate_card_title,
        active: activeValue,
      })
        .then(() => {
          const message = activeValue
            ? this.$t('ratecard_activated_successfully')
            : this.$t('ratecard_deactivated_successfully')
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](message)
          this.reloadData()
        })
        .catch((error) => {
          console.error('Error updating ratecard active status:', error)
        })
    },

    reloadData() {
      let options = { ...this.$route.query }

      if (this.options.sortBy.length > 0) {
        options.order = this.options.sortBy[0]
      }

      if (this.options.sortDesc.length > 0 && this.options.sortDesc[0]) {
        options.direction = 'DESC'
      }

      options.limit = this.options.itemsPerPage
      options.offset = this.options.itemsPerPage * (this.options.page - 1)

      this.loading = true
      RateCardAPI.getAllRateCards(options)
        .then((result) => {
          this.ratecards = result.data.items || []
          this.itemsLength = result.data.total || 0
          this.loading = false
        })
        .catch(() => {
          this.loading = false
        })
    },

    getModalTitle() {
      return this.selectedRatecard ? this.$t('edit_ratecard') : this.$t('new_ratecard')
    },

    getTenantTitle(tenantId) {
      const tenant = this.tenants.find(item => item.tenant_id == tenantId)
      return tenant ? (tenant.title || tenant.tenant_title) : String(tenantId || '')
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleDateString()
    },
  },
}
</script>
